import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerApi } from "@/lib/customerApi";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  CONSUMER_MIN_PASSWORD_LENGTH,
  consumerErrorMessage,
  readFragmentToken,
} from "@/lib/consumerCommerce";

export default function SecureMacProResetPassword() {
  const location = useLocation();
  const { refresh } = useCustomerAuth();
  const token = useRef(null);
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (token.current !== null) return;
    token.current = readFragmentToken(location.hash);
    // Keep the one-time token out of the address bar and history.
    window.history.replaceState(null, "", location.pathname);
    setReady(true);
  }, [location.hash, location.pathname]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("The passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await customerApi.post("/customers/password/complete-reset", {token:token.current,password});
      await refresh();
      setDone(true);
    } catch (err) {
      setError(
        err?.response?.status === 400
          ? "This reset link has expired or was already used. Request a new one from your account page."
          : consumerErrorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-ink-50/40 text-ink-900">
      <SEO title="Reset your password — LightningFlow" description="Choose a new LightningFlow account password." noIndex />
      <section className="lf-section min-h-[60vh]">
        <div className="lf-container">
          <div className="mx-auto max-w-md border border-ink-100 bg-white p-7 md:p-9" data-testid="reset-password-page">
            <div className="lf-eyebrow">LightningFlow account</div>
            {!ready ? null : done ? (
              <>
                <h1 className="mt-3 font-display text-2xl font-black" data-testid="reset-password-done">Password updated.</h1>
                <p className="mt-3 text-sm text-ink-600">Your browser sessions have been signed out. Sign in with your new password.</p>
                <Link className="lf-btn-primary mt-6 inline-flex" to="/account">Sign in</Link>
              </>
            ) : !token.current ? (
              <>
                <h1 className="mt-3 font-display text-2xl font-black" data-testid="reset-password-invalid">This reset link isn't valid.</h1>
                <p className="mt-3 text-sm text-ink-600">Request a new link from your account page.</p>
                <Link className="lf-btn-primary mt-6 inline-flex" to="/account">Go to your account</Link>
              </>
            ) : (
              <form className="mt-4 space-y-4" onSubmit={submit}>
                <h1 className="font-display text-2xl font-black">Choose a new password</h1>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">
                    New password (at least {CONSUMER_MIN_PASSWORD_LENGTH} characters)
                  </span>
                  <input className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm" type="password" required minLength={CONSUMER_MIN_PASSWORD_LENGTH} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">Confirm new password</span>
                  <input className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm" type="password" required minLength={CONSUMER_MIN_PASSWORD_LENGTH} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm" />
                </label>
                {error ? <p className="text-sm text-red-600" role="alert" data-testid="reset-password-error">{error}</p> : null}
                <button className="lf-btn-primary w-full justify-center" type="submit" disabled={busy} data-testid="reset-password-submit">
                  {busy ? "Updating…" : "Update password"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
